import React, { useState, useRef, useEffect } from "react";
import { useRegion } from "../context/RegionContext";
import { ChevronDown, Globe } from "lucide-react";

export default function RegionSelector({ variant = "navbar" }) {
    const { region, setRegion, allRegions } = useRegion();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isFooter = variant === "footer";

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border cursor-pointer ${
                    isFooter
                        ? "bg-white/5 hover:bg-white/10 text-slate-300 border-white/10 hover:border-white/20"
                        : "bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 shadow-sm"
                }`}
                aria-haspopup="true"
                aria-expanded={isOpen}
                title="Change Region & Currency"
            >
                <span className="text-sm leading-none">{region.flag}</span>
                <span className="font-mono tracking-tight">{region.currency}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
                <div
                    className={`absolute z-50 w-56 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl py-2 animate-in fade-in zoom-in-95 duration-150 ${
                        isFooter ? "bottom-full mb-2 left-0 sm:left-auto sm:right-0" : "top-full mt-2 right-0"
                    }`}
                >
                    <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-1.5 text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 tracking-wider">
                        <Globe className="w-3 h-3" /> Select Region & Currency
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                        {Object.values(allRegions).map((r) => {
                            const isSelected = r.code === region.code;
                            return (
                                <button
                                    key={r.code}
                                    type="button"
                                    onClick={() => {
                                        setRegion(r.code);
                                        setIsOpen(false);
                                    }}
                                    className={`w-full flex items-center justify-between px-3.5 py-2 text-xs text-left transition-colors cursor-pointer ${
                                        isSelected
                                            ? "bg-accent/10 dark:bg-accent/20 text-accent font-bold"
                                            : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 font-medium"
                                    }`}
                                >
                                    <div className="flex items-center gap-2.5 truncate">
                                        <span className="text-base">{r.flag}</span>
                                        <div className="truncate">
                                            <div className="truncate font-semibold">{r.name}</div>
                                            <div className="text-[10px] text-slate-400 dark:text-slate-500">{r.taxName} • {r.currency}</div>
                                        </div>
                                    </div>
                                    <span className="font-mono text-[11px] font-bold shrink-0 ml-2">
                                        {r.symbol}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
